// Little-endian binary codec over DataView (§20.1). Integer writes round and saturate to the
// field range (NaN writes 0) so out-of-range counters clamp instead of wrapping; `ang8` wraps by
// design. Reads past the end throw a RangeError.
import { TAU } from '../math';

function toInt(v: number, lo: number, hi: number): number {
  if (Number.isNaN(v)) return 0;
  const r = Math.round(v);
  return r < lo ? lo : r > hi ? hi : r;
}

export class BinWriter {
  private buf: ArrayBuffer;
  private view: DataView;
  private pos = 0;

  constructor(initialCapacity = 256) {
    this.buf = new ArrayBuffer(Math.max(16, initialCapacity));
    this.view = new DataView(this.buf);
  }

  /** Bytes written so far. */
  get offset(): number {
    return this.pos;
  }

  reset(): void {
    this.pos = 0;
  }

  private ensure(n: number): void {
    const need = this.pos + n;
    if (need <= this.buf.byteLength) return;
    let cap = this.buf.byteLength * 2;
    while (cap < need) cap *= 2;
    const next = new ArrayBuffer(cap);
    new Uint8Array(next).set(new Uint8Array(this.buf, 0, this.pos));
    this.buf = next;
    this.view = new DataView(next);
  }

  u8(v: number): void {
    this.ensure(1);
    this.view.setUint8(this.pos, toInt(v, 0, 0xff));
    this.pos += 1;
  }

  i8(v: number): void {
    this.ensure(1);
    this.view.setInt8(this.pos, toInt(v, -0x80, 0x7f));
    this.pos += 1;
  }

  u16(v: number): void {
    this.ensure(2);
    this.view.setUint16(this.pos, toInt(v, 0, 0xffff), true);
    this.pos += 2;
  }

  i16(v: number): void {
    this.ensure(2);
    this.view.setInt16(this.pos, toInt(v, -0x8000, 0x7fff), true);
    this.pos += 2;
  }

  u32(v: number): void {
    this.ensure(4);
    this.view.setUint32(this.pos, toInt(v, 0, 0xffffffff), true);
    this.pos += 4;
  }

  i32(v: number): void {
    this.ensure(4);
    this.view.setInt32(this.pos, toInt(v, -0x80000000, 0x7fffffff), true);
    this.pos += 4;
  }

  f32(v: number): void {
    this.ensure(4);
    this.view.setFloat32(this.pos, v, true);
    this.pos += 4;
  }

  /** Symmetric signed byte: round(v × scale) clamped to ±127 (input axes, §20.3). */
  q8(v: number, scale: number): void {
    this.i8(toInt(v * scale, -0x7f, 0x7f));
  }

  /** i16 = round(v × scale), clamped. */
  q16(v: number, scale: number): void {
    this.i16(v * scale);
  }

  /** u8 = round(rad / 2π × 256) & 255. */
  ang8(rad: number): void {
    this.ensure(1);
    this.view.setUint8(this.pos, Math.round((rad / TAU) * 256) & 0xff);
    this.pos += 1;
  }

  bytes(src: Uint8Array): void {
    this.ensure(src.byteLength);
    new Uint8Array(this.buf, this.pos, src.byteLength).set(src);
    this.pos += src.byteLength;
  }

  /** View of the written bytes; valid until the next write or reset. */
  toBytes(): Uint8Array {
    return new Uint8Array(this.buf, 0, this.pos);
  }
}

export class BinReader {
  private readonly view: DataView;
  private pos = 0;

  constructor(data: ArrayBuffer | ArrayBufferView) {
    this.view = data instanceof ArrayBuffer ? new DataView(data) : new DataView(data.buffer, data.byteOffset, data.byteLength);
  }

  get offset(): number {
    return this.pos;
  }

  get length(): number {
    return this.view.byteLength;
  }

  get remaining(): number {
    return this.view.byteLength - this.pos;
  }

  u8(): number {
    const v = this.view.getUint8(this.pos);
    this.pos += 1;
    return v;
  }

  i8(): number {
    const v = this.view.getInt8(this.pos);
    this.pos += 1;
    return v;
  }

  u16(): number {
    const v = this.view.getUint16(this.pos, true);
    this.pos += 2;
    return v;
  }

  i16(): number {
    const v = this.view.getInt16(this.pos, true);
    this.pos += 2;
    return v;
  }

  u32(): number {
    const v = this.view.getUint32(this.pos, true);
    this.pos += 4;
    return v;
  }

  i32(): number {
    const v = this.view.getInt32(this.pos, true);
    this.pos += 4;
    return v;
  }

  f32(): number {
    const v = this.view.getFloat32(this.pos, true);
    this.pos += 4;
    return v;
  }

  q8(scale: number): number {
    return this.i8() / scale;
  }

  q16(scale: number): number {
    return this.i16() / scale;
  }

  /** Radians in [−π, π). */
  ang8(): number {
    const v = this.u8();
    return ((v >= 128 ? v - 256 : v) / 256) * TAU;
  }
}
